"use client";

import { useState, useSyncExternalStore } from "react";
import { toast } from "@/components/toast";
import { loadGeocodingLibrary } from "@/lib/google-maps";
import type { LocationSelection } from "../lib/types";

const GEOLOCATION_ERROR_MESSAGES: Record<number, string> = {
  1: "Location access was denied. Enable location permissions and try again.",
  2: "Your location could not be determined. Please try again.",
  3: "Finding your location took too long. Please try again.",
};

let hasDeviceLocation = false;
const listeners = new Set<() => void>();

const markDeviceLocated = () => {
  hasDeviceLocation = true;
  listeners.forEach((notify) => notify());
};

const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => listeners.delete(notify);
};

/** True once the browser has handed us a position this session. */
export const useHasDeviceLocation = () =>
  useSyncExternalStore(
    subscribe,
    () => hasDeviceLocation,
    () => false,
  );

const SUBURB_TYPES = ["locality", "postal_town", "sublocality", "administrative_area_level_2"] as const;

const findComponent = (
  components: google.maps.GeocoderAddressComponent[],
  types: readonly string[],
): google.maps.GeocoderAddressComponent | undefined =>
  types.reduce<google.maps.GeocoderAddressComponent | undefined>(
    (found, type) => found ?? components.find((component) => component.types.includes(type)),
    undefined,
  );

const toSuburbLabel = (results: google.maps.GeocoderResult[]): string => {
  const components = results.flatMap((result) => result.address_components);
  const isPresent = (part: string | undefined): part is string => Boolean(part);

  const suburb = findComponent(components, SUBURB_TYPES)?.long_name;
  const state = findComponent(components, ["administrative_area_level_1"])?.short_name;
  const postcode = findComponent(components, ["postal_code"])?.long_name;
  const country = findComponent(components, ["country"])?.long_name;

  const locality = [suburb, state, postcode].filter(isPresent).join(" ");
  return [locality, country].filter(isPresent).join(", ");
};

/** Reads the browser's GPS position, then reverse-geocodes it to a human-readable address so the
 *  result reads the same whether it came from typing or from the GPS button. Every failure mode
 *  (unsupported browser, denied permission, geocoding error) is toasted and resolves to `null`
 *  (or, once we already have coordinates, to an address-less selection) instead of throwing. */
export const useCurrentLocation = () => {
  const [isLocating, setIsLocating] = useState(false);

  const locate = (): Promise<LocationSelection | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by this browser.");
        resolve(null);
        return;
      }

      setIsLocating(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coordinates = { lat: position.coords.latitude, lng: position.coords.longitude };
          markDeviceLocated();

          try {
            const { Geocoder } = await loadGeocodingLibrary();
            const { results } = await new Geocoder().geocode({ location: coordinates });
            resolve({ address: toSuburbLabel(results), coordinates });
          } catch {
            toast.error("We found your location but couldn't resolve it to an address.");
            resolve({ address: "", coordinates });
          } finally {
            setIsLocating(false);
          }
        },
        (positionError) => {
          toast.error(GEOLOCATION_ERROR_MESSAGES[positionError.code] ?? "We couldn't access your location.");
          setIsLocating(false);
          resolve(null);
        },
      );
    });

  return { locate, isLocating };
};
